import React, { useEffect, useState } from "react";

import { makeStyles, Button, Input, Label, useToastController } from "@fluentui/react-components";
import { useTextStyles, card } from "../styles/index";
import { notify } from "../components/Toast";

import { Screen } from "../../utils/common/constants";
import ReportUtil from "../../utils/report-util";
import { ReportAPI } from "../../apiclient/report";
import { FundAPI } from "../../apiclient/fund";
import FwDropdown from "../components/FwDropdown";
import { Fund, ReportType } from "@fundwave/openapi-client/dist/models";

interface TReportState {
  reportTypes: ReportType[];
  loading: boolean;
  selectedReportTypeId: string;
  reportName: string;
  savingReport: boolean;
  invalidReportName: boolean;
}

interface TFundState {
  funds: Fund[];
  loading: boolean;
  selectedFundId: string;
}

const useStyles = makeStyles({
  container: {
    ...card,
    flexDirection: "column",
    gap: "15px",
    width: "inherit"
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%"
  },
  actionContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "15px"
  }
});

const SaveReport: React.FC<{ navigate: Function }> = ({ navigate }) => {
  const styles = useStyles();
  const textStyles = useTextStyles();
  const [reportState, updateReportState] = useState<TReportState>({
    reportTypes: [],
    loading: false,
    selectedReportTypeId: "",
    reportName: "",
    savingReport: false,
    invalidReportName: false
  });
  const [fundState, updateFundState] = useState<TFundState>({
    funds: [],
    loading: false,
    selectedFundId: ""
  });

  const { dispatchToast } = useToastController();

  useEffect(() => {
    fetchReportTypes();
  }, [fundState.selectedFundId]);

  useEffect(() => {
    fetchFunds();
  }, []);

  async function fetchReportTypes() {
    if (!fundState.selectedFundId) return;
    try {
      updateReportState({ ...reportState, loading: true });

      const reportTypes = await ReportAPI.getReportTypes(fundState.selectedFundId);
      updateReportState({ ...reportState, reportTypes, loading: false, selectedReportTypeId: reportTypes?.[0]?.id || "" });
    } catch (error) {
      console.error(`Error while fetching report types ${error}`);
      updateReportState({ ...reportState, loading: false });
      dispatchToast(notify("Unable to fetch report types!"), { intent: "error" });
    }
  }

  async function fetchFunds() {
    try {
      updateFundState({ ...fundState, loading: true });
      updateReportState({ ...reportState, loading: true });

      const funds = await FundAPI.getFundsOfUser();

      updateFundState({ ...fundState, funds: funds, loading: false, selectedFundId: funds?.[0]?.id });
    } catch (error) {
      console.error(`Error while fetching funds, ${error}`);
      dispatchToast(notify("Unable to fetch funds!"), { intent: "error" });
      updateFundState({ ...fundState, loading: false });
      updateReportState({ ...reportState, loading: false });
    }
  }

  const handleSave = async () => {
    try {
      if (!reportState.reportName?.trim()) {
        updateReportState({ ...reportState, invalidReportName: true });
        return;
      }
      updateReportState({ ...reportState, savingReport: true });

      await ReportUtil.saveReportToFW(fundState.selectedFundId, reportState.reportName, reportState.selectedReportTypeId);
      dispatchToast(notify("Report saved successfully."), { intent: "success" });
      navigate(Screen.Home);
    } catch (error) {
      console.error(`Error while saving report, ${error}`);
      dispatchToast(notify("Unable to save report."), { intent: "error" });
      updateReportState({ ...reportState, savingReport: false });
    }
  };

  const handleCancel = () => navigate(Screen.Home);

  return (
    <div className={styles.container}>
      <h2 className={textStyles.heading1}>Save Report</h2>

      <div className={styles.formGroup}>
        <Label className={textStyles.label}>Report Name</Label>
        <Input
          placeholder="Enter report name"
          value={reportState.reportName}
          onChange={(e) => updateReportState({ ...reportState, invalidReportName: !e.target.value?.trim(), reportName: e.target.value })}
        />
        {reportState.invalidReportName && <span className={textStyles.error}>Please enter filename</span>}
      </div>

      <FwDropdown
        className={styles.formGroup}
        options={fundState.funds}
        label="Fund"
        placeholder="Select fund"
        isLoading={fundState.loading}
        defaultValue={fundState.funds?.[0]?.name}
        defaultSelectedOptions={[fundState.funds?.[0]?.id || ""]}
        onOptionSelect={(_, data) => updateFundState({ ...fundState, selectedFundId: data.optionValue || "" })}
        idLabel="name"
        idValue="id"
      />

      <FwDropdown
        className={styles.formGroup}
        options={reportState.reportTypes}
        label="Report Type"
        placeholder="Select report type"
        isLoading={reportState.loading}
        defaultValue={reportState.reportTypes?.[0]?.name}
        defaultSelectedOptions={[reportState.reportTypes?.[0]?.id || ""]}
        onOptionSelect={(_, data) => updateReportState({ ...reportState, selectedReportTypeId: data.optionValue || "" })}
        idLabel="name"
        idValue="id"
        emptyLabel="No report types found, reports will be saved in Miscellaneous"
      />

      <div className={styles.actionContainer}>
        <Button disabled={reportState.savingReport} appearance="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button disabled={reportState.savingReport} appearance="primary" onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );
};

export default SaveReport;